import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./css/NavMenu.module.css";
import { navLinks } from "./data";
import { getHashId, scrollToId, useNavClick } from "./navHelpers";

export default function NavMenu({ onItemClick, links = navLinks, variant = "desktop" }) {
    const [active, setActive] = useState("inicio");

    const ioRef = useRef(null);
    const moRef = useRef(null);
    const observedIdsRef = useRef(new Set());

    const handleClick = useNavClick(onItemClick);

    // Configura IntersectionObserver para realçar item ativo (apenas seções da home)
    const observeTargets = useCallback(() => {
        if (typeof window === "undefined") return;
        if (ioRef.current) ioRef.current.disconnect();

        ioRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActive(entry.target.id);
                });
            },
            { threshold: 0.6 }
        );

        observedIdsRef.current.clear();
        links.forEach(({ path }) => {
            const id = getHashId(path);
            if (!id) return;
            const node = document.getElementById(id);
            if (node) {
                ioRef.current.observe(node);
                observedIdsRef.current.add(id);
            }
        });
    }, [links]);

    // Observa o DOM para seções que apareçam depois (lazy mount)
    const attachMutationObserver = useCallback(() => {
        if (typeof window === "undefined") return;
        if (moRef.current) moRef.current.disconnect();

        const needIds = new Set(
            links.map((l) => getHashId(l.path)).filter(Boolean)
        );
        moRef.current = new MutationObserver(() => {
            if (!ioRef.current) return;
            needIds.forEach((id) => {
                if (observedIdsRef.current.has(id)) return;
                const node = document.getElementById(id);
                if (node) {
                    ioRef.current.observe(node);
                    observedIdsRef.current.add(id);
                }
            });
        });

        moRef.current.observe(document.body, { childList: true, subtree: true });
    }, [links]);

    useEffect(() => {
        observeTargets();
        attachMutationObserver();
        return () => {
            if (ioRef.current) ioRef.current.disconnect();
            if (moRef.current) moRef.current.disconnect();
        };
    }, [observeTargets, attachMutationObserver]);

    // Se entrar com hash (inclusive vindo de outra página), corrige a posição
    useEffect(() => {
        if (typeof window === "undefined") return;
        const { hash } = window.location;
        if (!hash) return;
        // espera o layout estabilizar; tenta algumas vezes pois seções podem
        // montar de forma assíncrona (ex.: navegação vinda do blog).
        let tries = 0;
        const id = hash.slice(1);
        const attempt = () => {
            const ok = scrollToId(id, "auto");
            tries += 1;
            if (!ok && tries < 10) setTimeout(attempt, 80);
        };
        setTimeout(attempt, 0);
    }, []);

    const items = useMemo(
        () =>
            links.map((link) => {
                const id = getHashId(link.path);
                const isActive = id && active === id;
                const isCta = link.cta;
                const linkClass =
                    variant === "mobile"
                        ? `${styles.mobileLink} ${isCta ? styles.mobileCta : ""}`
                        : `${styles.navLink} ${isActive ? styles.active : ""} ${
                              isCta ? styles.ctaLink : ""
                          }`;

                return (
                    <li
                        key={link.path}
                        className={
                            variant === "mobile" ? styles.mobileItem : styles.navItem
                        }
                    >
                        <a
                            href={link.path}
                            onClick={(e) => handleClick(e, link.path)}
                            className={linkClass}
                            aria-current={isActive ? "page" : undefined}
                        >
                            {link.label}
                        </a>
                    </li>
                );
            }),
        [active, handleClick, links, variant]
    );

    return (
        <ul className={variant === "mobile" ? styles.mobileList : styles.navList}>
            {items}
        </ul>
    );
}
